import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { combineLatest, filter, firstValueFrom, ReplaySubject, timer } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class SwManagerService {
  private registration: ServiceWorkerRegistration | null = null;
  private initialized$ = new ReplaySubject<boolean>(1);

  constructor(private authService: AuthService) {
    this.init().then(() => {
      this.setJwtToken();
    });
    combineLatest([
      this.initialized$.pipe(filter(initialized => initialized === true)),
      timer(0, 1000 * 60 * 5),
    ]).subscribe(async () => {
      this.setJwtToken();
    })
  }

  async setJwtToken() {
    const token = await this.authService.getToken();
    if (navigator?.serviceWorker?.controller) {
      navigator?.serviceWorker?.controller.postMessage({
        type: 'JWT_TOKEN_RESPONSE',
        token: token,
      });
    }
  }

  private async init() {
    if (!Capacitor.isNativePlatform() && 'serviceWorker' in navigator) {
      try {
        this.registration = await navigator?.serviceWorker?.register('/sw.js', {
          scope: '/'
        });

        console.log('Service Worker registered successfully:', this.registration);

        // Listen for messages from service worker
        navigator?.serviceWorker?.addEventListener('message', this.handleSWMessage.bind(this));

        // Handle updates
        this.registration.addEventListener('updatefound', () => {
          const newWorker = this.registration!.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator?.serviceWorker?.controller) {
                // New service worker is available
                console.log('New service worker available');
                // You could show a notification to user to refresh
              }
            });
          }
        });
        this.initialized$.next(true);
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        this.initialized$.next(false);
      }
    } else {
      this.initialized$.next(false);
    }
  }

  public async preCacheImages(urls: string[], chunkSize: number = 3): Promise<void> {
    const initialized = await firstValueFrom(this.initialized$);
    if (initialized === false) {
      return;
    }
    if (!navigator?.serviceWorker?.controller) {
      console.warn('SwManagerService: No active service worker available for pre-caching');
      return;
    }

    console.log('SwManagerService: Pre-caching', urls.length, 'images in chunks of', chunkSize);

    // Split URLs into chunks
    const chunks = this.chunkArray(urls, chunkSize);

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      console.log(`Processing chunk ${i + 1}/${chunks.length}:`, chunk.length, 'images');

      // Send chunk to service worker and wait for completion
      await this.processCacheChunk(chunk);
    }

    console.log('SwManagerService: All chunks processed');
  }

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  private processCacheChunk(urls: string[]): Promise<void> {
    return new Promise((resolve) => {
      const chunkId = Date.now() + '_' + Math.random();
      
      // Set up listener for this specific chunk completion
      const chunkCompleteHandler = (event: MessageEvent) => {
        if (event.data.type === 'CHUNK_COMPLETE' && event.data.chunkId === chunkId) {
          navigator?.serviceWorker?.removeEventListener('message', chunkCompleteHandler);
          resolve();
        }
      };

      navigator?.serviceWorker?.addEventListener('message', chunkCompleteHandler);

      // Send chunk to service worker
      navigator?.serviceWorker?.controller!.postMessage({
        type: 'PRE_CACHE_CHUNK',
        urls: urls,
        chunkId: chunkId
      });
    });
  }


  private async handleSWMessage(event: MessageEvent) {
    const { type, requestId } = event.data;
    if (event.data.type === 'GET_JWT_TOKEN') {
      // Get JWT token from your auth service or localStorage
      const token = await this.authService.getToken();;

      if (navigator?.serviceWorker?.controller) {
        navigator?.serviceWorker?.controller.postMessage({
          type: 'JWT_TOKEN_RESPONSE',
          token: token,
          requestId: requestId
        });
      }

    }
  }
  // Method to manually update the service worker
  public async updateServiceWorker() {
    if (this.registration) {
      await this.registration.update();
    }
  }

  // Method to skip waiting and activate new service worker
  public skipWaiting() {
    if (navigator?.serviceWorker?.controller) {
      navigator?.serviceWorker?.controller.postMessage({ type: 'SKIP_WAITING' });
    }
  }
}